import React, { useMemo } from 'react';
import styles from './PasswordStrength.module.scss';

interface PasswordRule {
  label: string;
  test: (value: string) => boolean;
}

// Same four signals the auth concept scores against — order matches the
// requirement checklist below.
const RULES: PasswordRule[] = [
  { label: '8+ characters', test: (v) => v.length >= 8 },
  { label: 'One number', test: (v) => /\d/.test(v) },
  { label: 'Upper and lower case', test: (v) => /[a-z]/.test(v) && /[A-Z]/.test(v) },
  { label: 'One symbol', test: (v) => /[^\w\s]/.test(v) },
];

const SCORE_LABEL = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'] as const;
const SCORE_TONE = ['idle', 'weak', 'fair', 'good', 'strong'] as const;

function scorePassword(value: string): number {
  if (!value) return 0;
  return RULES.filter((rule) => rule.test(value)).length;
}

interface PasswordStrengthProps {
  value: string;
}

/** Password strength meter + requirement checklist — reused by Register and ResetPasswordConfirm. */
export const PasswordStrength: React.FC<PasswordStrengthProps> = ({ value }) => {
  const score = useMemo(() => scorePassword(value), [value]);
  const tone = SCORE_TONE[score];

  return (
    <div className={styles.strength} aria-live="polite">
      <div className={styles.label}>
        <span>Password strength</span>
        {value && <span className={styles[`label--${tone}`]}>{SCORE_LABEL[score]}</span>}
      </div>

      <div className={styles.bars}>
        {RULES.map((rule, i) => (
          <span
            key={rule.label}
            className={`${styles.bar} ${i < score ? styles[`bar--${tone}`] : ''}`}
          />
        ))}
      </div>

      <div className={styles.requirements}>
        {RULES.map((rule) => {
          const met = rule.test(value);
          return (
            <span key={rule.label} className={`${styles.requirement} ${met ? styles['requirement--met'] : ''}`}>
              <i className={styles.requirementIcon} aria-hidden="true">
                {met ? '✓' : ''}
              </i>
              {rule.label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default PasswordStrength;
